import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Heading, Flex, Button, Spinner, Tag, Badge, HStack, Text,
  Input, Select, useToast, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Textarea, Table, Thead, Tbody, Tr, Th, Td, IconButton, ButtonGroup, Avatar,
  AvatarGroup, useMediaQuery,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import KanbanBoard from "./KanbanBoard";
import TaskDetailDrawer from "./TaskDetailDrawer";
import DfListView from "./DfListView";
import { FUNCTIONAL_DEPARTMENTS, FUNCTIONAL_ROLE_LABELS, FUNCTIONAL_ROLE_COLORS } from "./df-constants";
import { Checkbox } from "@chakra-ui/react";
import TaskCreateModalV2 from "./TaskCreateModalV2";
import DeadlineBoard from "./DeadlineBoard";

const STATUSES = ["todo", "in_progress", "review", "done"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

export default function TasksPage() {
  const toast = useToast();
  const [view, setView] = useState(() => localStorage.getItem("tasksView") || "deadline");
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", projectId: "", status: "", priority: "", mine: true });
  const [openTaskId, setOpenTaskId] = useState(null);
  const drawerDisc = useDisclosure();
  const createDisc = useDisclosure();

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.projectId) params.set("projectId", filters.projectId);
      if (filters.status) params.set("status", filters.status);
      if (filters.priority) params.set("priority", filters.priority);
      if (filters.mine) params.set("mine", "1");

      const [t, p, u] = await Promise.all([
        api(`/tasks?${params.toString()}`),
        api("/projects").catch(() => ({ projects: [] })),
        api("/admin/users").catch(() => ({ users: [] })),
      ]);
      setTasks(t.tasks || []);
      setProjects(p.projects || []);
      setUsers(u.users || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function openTask(task) {
    setOpenTaskId(task._id);
    drawerDisc.onOpen();
  }
  function closeDrawer() {
    drawerDisc.onClose();
    setOpenTaskId(null);
    load();
  }

  useShellHeader({
    title: "Tasks",
    actions: <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={createDisc.onOpen}>New Task</Button>,
  }, [createDisc]);

  return (
    <Box>
      <HStack mb={4} spacing={3} wrap="wrap">
        <Input placeholder="Search tasks" value={filters.q} bg="white" maxW="220px"
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()} />
        <Select placeholder="All projects" value={filters.projectId} bg="white" maxW="200px"
          onChange={(e) => setFilters({ ...filters, projectId: e.target.value })}>
          {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </Select>
        <Select placeholder="All statuses" value={filters.status} bg="white" maxW="160px"
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select placeholder="All priorities" value={filters.priority} bg="white" maxW="160px"
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
        <Button size="sm" variant={filters.mine ? "solid" : "outline"} colorScheme="blue"
          onClick={() => setFilters({ ...filters, mine: !filters.mine })}>
          Mine
        </Button>
        <Button onClick={load}>Apply</Button>
        <Box flex="1" />
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            onClick={() => { setView("deadline"); localStorage.setItem("tasksView", "deadline"); }}
            variant={view === "deadline" ? "solid" : "outline"}
            colorScheme="blue"
          >Deadline</Button>
          <Button
            onClick={() => { setView("list"); localStorage.setItem("tasksView", "list"); }}
            variant={view === "list" ? "solid" : "outline"}
            colorScheme="blue"
          >List</Button>
          <Button
            onClick={() => { setView("kanban"); localStorage.setItem("tasksView", "kanban"); }}
            variant={view === "kanban" ? "solid" : "outline"}
            colorScheme="blue"
          >Board</Button>
        </ButtonGroup>
      </HStack>

      {loading ? <Spinner /> : (
        view === "deadline" ? (
          <DeadlineBoard
            tasks={tasks}
            members={users}
            onOpenTask={(id) => { setOpenTaskId(id); drawerDisc.onOpen(); }}
            onTasksChange={setTasks}
          />
        ) : view === "kanban" ? (
          <KanbanBoard tasks={tasks} onTaskClick={(t) => openTask(t)} onChange={setTasks} />
        ) : (
          <DfListView tasks={tasks} members={users} onOpenTask={(id) => { setOpenTaskId(id); drawerDisc.onOpen(); }} onTasksChange={setTasks} />
        )
      )}

      <TaskDetailDrawer
        isOpen={drawerDisc.isOpen}
        onClose={closeDrawer}
        taskId={openTaskId}
        allUsers={users}
        onChanged={(updated) => {
          if (updated == null) {
            setTasks((prev) => prev.filter((t) => t._id !== openTaskId));
          } else {
            setTasks((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
          }
        }}
      />

      <TaskCreateModalV2
        isOpen={createDisc.isOpen}
        onClose={createDisc.onClose}
        projects={projects}
        users={users}
        onCreated={(task) => setTasks((prev) => [task, ...prev])}
      />
    </Box>
  );
}

function TaskList({ tasks, onTaskClick }) {
  if (!tasks.length) return <Box bg="white" p={8} rounded="md" textAlign="center" color="gray.500">No tasks match these filters.</Box>;
  return (
    <Box bg="white" rounded="md" shadow="sm" overflow="auto">
      <Table size="sm">
        <Thead bg="gray.50">
          <Tr><Th>Title</Th><Th>Project</Th><Th>Status</Th><Th>Priority</Th><Th>Assignees</Th><Th>Due</Th></Tr>
        </Thead>
        <Tbody>
          {tasks.map((t) => (
            <Tr key={t._id} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => onTaskClick(t)}>
              <Td fontWeight={600}>{t.title}</Td>
              <Td>{t.projectId ? <Tag bg={t.projectId.color || "gray.300"} color="white">{t.projectId.name}</Tag> : "—"}</Td>
              <Td><Badge colorScheme={t.status === "done" ? "green" : t.status === "in_progress" ? "blue" : "gray"}>{t.status.replace("_", " ")}</Badge></Td>
              <Td><Tag size="sm" colorScheme={{low:"gray",normal:"blue",high:"orange",urgent:"red"}[t.priority]}>{t.priority}</Tag></Td>
              <Td><AvatarGroup size="xs" max={3}>{(t.assignees || []).map((a) => <Avatar key={a._id} name={a.username} />)}</AvatarGroup></Td>
              <Td fontSize="xs" color="gray.500">{t.dueDate ? new Date(t.dueDate).toLocaleDateString() : "—"}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function CreateTaskModal({ isOpen, onClose, projects, users, onCreated, defaultProjectId }) {
  const toast = useToast();
  const empty = { title: "", description: "", priority: "normal", status: "todo", projectId: defaultProjectId || "", dueDate: "", assignees: [], departmentSubtasks: {} };
  const [f, setF] = useState(empty);
  useEffect(() => {
    setF((p) => ({ ...p, projectId: defaultProjectId || "" }));
  }, [defaultProjectId, isOpen]);

  function toggleDept(d) {
    setF((p) => {
      const next = { ...p.departmentSubtasks };
      if (d in next) delete next[d];
      else next[d] = "";
      return { ...p, departmentSubtasks: next };
    });
  }

  async function submit() {
    try {
      const departmentSubtasks = Object.entries(f.departmentSubtasks || {}).map(([department, assigneeId]) => ({
        department,
        assigneeId: assigneeId || null,
      }));
      const body = {
        title: f.title,
        description: f.description,
        status: f.status,
        priority: f.priority,
        projectId: f.projectId || null,
        dueDate: f.dueDate || null,
        assignees: f.assignees,
        departmentSubtasks: departmentSubtasks.length ? departmentSubtasks : undefined,
      };
      const r = await api("/tasks", { method: "POST", body: JSON.stringify(body) });
      toast({ title: departmentSubtasks.length ? `Task + ${departmentSubtasks.length} dept subtasks created` : "Task created", status: "success" });
      onCreated && onCreated(r.task);
      onClose();
      setF(empty);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Task</ModalHeader><ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}><FormLabel>Title</FormLabel>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus />
          </FormControl>
          <FormControl mb={3}><FormLabel>Description</FormLabel>
            <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </FormControl>
          <HStack mb={3}>
            <FormControl><FormLabel>Project</FormLabel>
              <Select placeholder="None" value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })} isDisabled={!!defaultProjectId}>
                {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </Select>
            </FormControl>
            <FormControl><FormLabel>Status</FormLabel>
              <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </FormControl>
            <FormControl><FormLabel>Priority</FormLabel>
              <Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </FormControl>
          </HStack>
          <HStack>
            <FormControl><FormLabel>Due date</FormLabel>
              <Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
            </FormControl>
            <FormControl><FormLabel>Assign (top-level)</FormLabel>
              <Select placeholder="None" value={f.assignees[0] || ""} onChange={(e) => setF({ ...f, assignees: e.target.value ? [e.target.value] : [] })}>
                {users.map((u) => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
              </Select>
            </FormControl>
          </HStack>

          <Box mt={4} p={3} bg="gray.50" rounded="md">
            <Text fontSize="xs" fontWeight={600} color="gray.700" mb={1}>Department subtasks (optional)</Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Auto-creates one subtask per department. Each can be assigned independently.
            </Text>
            {FUNCTIONAL_DEPARTMENTS.map((d) => {
              const enabled = d in (f.departmentSubtasks || {});
              return (
                <HStack key={d} mb={2} spacing={3}>
                  <Checkbox isChecked={enabled} onChange={() => toggleDept(d)}>
                    <HStack>
                      <Box w="8px" h="8px" rounded="full" bg={FUNCTIONAL_ROLE_COLORS[d]} />
                      <Text fontSize="sm">{FUNCTIONAL_ROLE_LABELS[d]}</Text>
                    </HStack>
                  </Checkbox>
                  {enabled && (
                    <Select size="sm" maxW="240px" placeholder="Unassigned"
                      value={f.departmentSubtasks[d] || ""}
                      onChange={(e) => setF({ ...f, departmentSubtasks: { ...f.departmentSubtasks, [d]: e.target.value } })}>
                      {users.map((u) => <option key={u._id} value={u._id}>{u.username}</option>)}
                    </Select>
                  )}
                </HStack>
              );
            })}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={submit} isDisabled={!f.title.trim()}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { CreateTaskModal };
